import { useEffect, useState } from 'react';
import CampoImporto from '../components/CampoImporto';
import CampoImpegno from '../components/CampoImpegno';
import { etichettaImpegnoConsulente } from '../components/impegno';
import { useParams, Link } from 'react-router-dom';
import { commesseApi, fatturazionIApi, costiConsulentiApi, offerteConsulentiApi, consulentiApi } from '../api';
import Modal from '../components/Modal';
import StatusBadge from '../components/StatusBadge';
import ProgressBar from '../components/ProgressBar';
import ConsulentiCommessa from '../components/ConsulentiCommessa';
import IncaricoConsulente from '../components/IncaricoConsulente';
import PannelloOrdini from '../components/PannelloOrdini';
import MandaInFatturazione from '../components/MandaInFatturazione';

const fmt = n => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n || 0);
const fmtDate = d => d ? new Date(d).toLocaleDateString('it-IT') : '—';

const STATI_FATT = ['da_emettere', 'emessa', 'pagata', 'annullata'];
const STATI_OC = ['attiva', 'completata', 'chiusa', 'annullata'];

export default function CommessaDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [consulenti, setConsulenti] = useState([]);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState('fatturazione');

  const load = () => commesseApi.get(id).then(setData);
  useEffect(() => { load(); consulentiApi.list().then(setConsulenti); }, [id]);

  if (!data) return <div className="text-gray-400 p-8">Caricamento...</div>;

  // "Fatturato" sono SOLO le fatture realmente emesse (o già pagate).
  // Sommare anche le righe "da emettere" faceva risultare fatturato l'intero
  // budget anche quando una parte era ancora da emettere.
  const totFatturato = data.fatturazioni
    .filter(f => ['emessa', 'pagata'].includes(f.stato))
    .reduce((s, f) => s + parseFloat(f.importo), 0);
  // Quanto del contratto è stato fatturato: le spese riaddebitate si sommano
  // alla fattura ma non consumano il budget concordato.
  const totContratto = data.fatturazioni
    .filter(f => ['emessa', 'pagata'].includes(f.stato) && !f.fuori_budget)
    .reduce((s, f) => s + parseFloat(f.importo), 0);
  const totSpeseRiaddebitate = totFatturato - totContratto;
  const totDaEmettere = data.fatturazioni
    .filter(f => f.stato === 'da_emettere')
    .reduce((s, f) => s + parseFloat(f.importo), 0);
  const totCosti = data.costi_consulenti.reduce((s, c) => s + parseFloat(c.importo), 0);
  const margine = totFatturato - totCosti;

  const openFatt = (r) => {
    setForm(r ? { ...r, data_emissione: r.data_emissione?.slice(0,10)||'', data_scadenza: r.data_scadenza?.slice(0,10)||'', data_pagamento: r.data_pagamento?.slice(0,10)||'' }
               : { commessa_id: id, numero_fattura: '', descrizione: '', importo: '', data_emissione: '', data_scadenza: '', stato: 'da_emettere', note: '' });
    setModal({ type: 'fatt', mode: r ? 'edit' : 'new', id: r?.id });
  };

  const openCosto = (r) => {
    setForm(r ? { ...r, data_imputazione: r.data_imputazione?.slice(0,10)||'' }
               : { commessa_id: id, consulente_id: '', offerta_consulente_id: '', descrizione: '', ore: '', importo: '', data_imputazione: new Date().toISOString().slice(0,10), nota_interna: '' });
    setModal({ type: 'costo', mode: r ? 'edit' : 'new', id: r?.id });
  };

  const openOC = (r) => {
    setForm(r ? { ...r, data_offerta: r.data_offerta?.slice(0,10)||'', data_scadenza: r.data_scadenza?.slice(0,10)||'' }
               : { commessa_id: id, consulente_id: '', numero: '', descrizione: '', importo_max: '', ore_previste: '', unita_previste: 'giorno', data_offerta: '', stato: 'attiva', note: '' });
    setModal({ type: 'oc', mode: r ? 'edit' : 'new', id: r?.id });
  };

  const save = async () => {
    setSaving(true);
    try {
      const { type, mode, id: rid } = modal;
      if (type === 'fatt') mode === 'new' ? await fatturazionIApi.create(form) : await fatturazionIApi.update(rid, form);
      if (type === 'costo') mode === 'new' ? await costiConsulentiApi.create(form) : await costiConsulentiApi.update(rid, form);
      if (type === 'oc') mode === 'new' ? await offerteConsulentiApi.create(form) : await offerteConsulentiApi.update(rid, form);
      setModal(null); load();
    } catch (e) { alert(e); } finally { setSaving(false); }
  };

  const del = async (type, rid) => {
    if (!confirm('Eliminare?')) return;
    if (type === 'fatt') await fatturazionIApi.delete(rid);
    if (type === 'costo') await costiConsulentiApi.delete(rid);
    if (type === 'oc') await offerteConsulentiApi.delete(rid);
    load();
  };

  const f = (k) => ({ value: form[k] ?? '', onChange: e => setForm(p => ({ ...p, [k]: e.target.value })) });

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <Link to="/commesse" className="text-sm text-brand-600 hover:underline">← Commesse</Link>
          <h1 className="page-title mt-1">{data.numero} — {data.titolo}</h1>
          <div className="text-sm text-gray-500 mt-0.5">{data.cliente_nome} · <StatusBadge stato={data.stato} /></div>
        </div>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="card text-center">
          <div className="text-xs text-gray-500">Budget</div>
          <div className="text-xl font-bold text-gray-900">{fmt(data.budget)}</div>
        </div>
        <div className="card text-center">
          <div className="text-xs text-gray-500">Fatturato</div>
          <div className="text-xl font-bold text-brand-600">{fmt(totFatturato)}</div>
          <ProgressBar value={totFatturato} max={data.budget} showLabel={false} />
        </div>
        <div className="card text-center">
          <div className="text-xs text-gray-500">Da emettere</div>
          <div className={`text-xl font-bold ${totDaEmettere > 0 ? 'text-amber-600' : 'text-gray-300'}`}>
            {fmt(totDaEmettere)}
          </div>
        </div>
        <div className="card text-center">
          <div className="text-xs text-gray-500">Costi consulenti</div>
          <div className="text-xl font-bold text-red-600">{fmt(totCosti)}</div>
        </div>
        <div className="card text-center">
          <div className="text-xs text-gray-500">Margine</div>
          <div className={`text-xl font-bold ${margine >= 0 ? 'text-green-600' : 'text-red-600'}`}>{fmt(margine)}</div>
        </div>
      </div>

      <ConsulentiCommessa commessaId={id} />

      {/* Tabs */}
      <div className="flex gap-1 border-b">
        {[
          { k: 'fatturazione', label: `Fatturazione (${data.fatturazioni.length})` },
          { k: 'ordini', label: `Ordini cliente (${data.ordini?.ordini?.length || 0})` },
          { k: 'oc', label: `Offerte consulenti (${data.offerte_consulenti.length})` },
          { k: 'costi', label: `Costi imputati (${data.costi_consulenti.length})` },
        ].map(t => (
          <button key={t.k} onClick={() => setTab(t.k)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === t.k ? 'border-brand-600 text-brand-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab Fatturazione */}
      {tab === 'fatturazione' && (
        <div className="space-y-3">
          {/* La strada normale è il SAL: si rendiconta l'avanzamento e
              l'approvazione genera la fattura da emettere. Scrivere una fattura
              a mano scollega l'importo dal lavoro che lo giustifica, quindi
              resta possibile ma in secondo piano. */}
          <div className="flex justify-end items-center gap-3">
            <button className="text-sm text-gray-500 hover:text-gray-700 hover:underline"
                    onClick={() => openFatt(null)}>
              fattura manuale
            </button>
            {/* Il lavoro a corpo non ha avanzamenti da rendicontare: si decide
                quanto fatturare e in che mese, senza passare da un SAL. */}
            <MandaInFatturazione commessaId={data.id} titolo={data.titolo} onCambiato={load} compatto />
            <Link to={`/sal?commessa=${data.id}&nuovo=1`} className="btn-primary">
              + Aggiungi SAL
            </Link>
          </div>
          <p className="text-xs text-gray-400 text-right -mt-1">
            A corpo: "Manda in fatturazione" crea le fatture da emettere nel mese che scegli.
            A giornate: l'approvazione di un SAL genera da sola la fattura, divisa fra consulenza e spese.
          </p>
          <div className="card p-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr className="text-xs text-gray-500 text-left">
                  <th className="px-4 py-3">N. Fattura</th>
                  <th className="px-4 py-3">Descrizione</th>
                  <th className="px-4 py-3">Ordine</th>
                  <th className="px-4 py-3">Importo</th>
                  <th className="px-4 py-3">Emissione</th>
                  <th className="px-4 py-3">Scadenza</th>
                  <th className="px-4 py-3">Pagamento</th>
                  <th className="px-4 py-3">Stato</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {data.fatturazioni.length === 0 && <tr><td colSpan={9} className="px-4 py-6 text-center text-gray-400">Nessuna fattura</td></tr>}
                {data.fatturazioni.map(r => (
                  <tr key={r.id} className="border-b last:border-0 hover:bg-gray-50">
                    <td className="px-4 py-3 font-mono text-gray-600">{r.numero_fattura || '—'}</td>
                    <td className="px-4 py-3">{r.descrizione}</td>
                    {/* Il numero d'ordine va citato in fattura: se la commessa
                        ne ha uno e la riga non lo porta, si vede subito. */}
                    <td className="px-4 py-3 text-xs">
                      {r.ordine_numero
                        ? <span className="font-mono text-gray-600">{r.ordine_numero}</span>
                        : data.ordini?.ha_ordini && !/^da fatturare/i.test(r.descrizione || '')
                          ? <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-medium">da collegare</span>
                          : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-4 py-3 font-medium">{fmt(r.importo)}</td>
                    <td className="px-4 py-3 text-gray-500">{fmtDate(r.data_emissione)}</td>
                    <td className="px-4 py-3 text-gray-500">{fmtDate(r.data_scadenza)}</td>
                    <td className="px-4 py-3 text-gray-500">{fmtDate(r.data_pagamento)}</td>
                    <td className="px-4 py-3"><StatusBadge stato={r.stato} /></td>
                    <td className="px-4 py-3 flex gap-1 justify-end">
                      <button className="btn-secondary btn-sm" onClick={() => openFatt(r)}>Modifica</button>
                      <button className="btn-danger btn-sm" onClick={() => del('fatt', r.id)}>Elimina</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Tab Ordini del cliente */}
      {tab === 'ordini' && (
        <PannelloOrdini commessaId={id} budget={parseFloat(data.budget) || 0} onCambiato={load} />
      )}

      {/* Tab Offerte Consulenti */}
      {tab === 'oc' && (
        <div className="space-y-3">
          <div className="flex justify-end"><button className="btn-primary" onClick={() => openOC(null)}>+ Aggiungi offerta consulente</button></div>
          <div className="card p-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr className="text-xs text-gray-500 text-left">
                  <th className="px-4 py-3">Consulente</th>
                  <th className="px-4 py-3">Numero</th>
                  <th className="px-4 py-3">Importo max</th>
                  <th className="px-4 py-3">Ore previste</th>
                  <th className="px-4 py-3">Imputato</th>
                  <th className="px-4 py-3 w-36">Avanzamento</th>
                  <th className="px-4 py-3">Stato</th>
                  <th className="px-4 py-3">Incarico</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {data.offerte_consulenti.length === 0 && <tr><td colSpan={9} className="px-4 py-6 text-center text-gray-400">Nessuna offerta consulente</td></tr>}
                {data.offerte_consulenti.map(r => (
                  <tr key={r.id} className="border-b last:border-0 hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium">{r.consulente_nome} {r.consulente_cognome}</td>
                    <td className="px-4 py-3 font-mono text-gray-500">{r.numero || '—'}</td>
                    <td className="px-4 py-3">{fmt(r.importo_max)}</td>
                    <td className="px-4 py-3 text-gray-600">{etichettaImpegnoConsulente(r.ore_previste, r.unita_previste) || '—'}</td>
                    <td className="px-4 py-3 font-medium text-red-600">{fmt(r.importo_imputato)}</td>
                    <td className="px-4 py-3"><ProgressBar value={r.importo_imputato} max={r.importo_max} /></td>
                    <td className="px-4 py-3"><StatusBadge stato={r.stato} /></td>
                    <td className="px-4 py-3"><IncaricoConsulente offerta={r} onCambiato={load} /></td>
                    <td className="px-4 py-3 flex gap-1 justify-end">
                      <button className="btn-secondary btn-sm" onClick={() => openOC(r)}>Modifica</button>
                      <button className="btn-danger btn-sm" onClick={() => del('oc', r.id)}>Elimina</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Tab Costi */}
      {tab === 'costi' && (
        <div className="space-y-3">
          <div className="flex justify-end"><button className="btn-primary" onClick={() => openCosto(null)}>+ Imputa costo</button></div>
          <div className="card p-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr className="text-xs text-gray-500 text-left">
                  <th className="px-4 py-3">Data</th>
                  <th className="px-4 py-3">Consulente</th>
                  <th className="px-4 py-3">Offerta rif.</th>
                  <th className="px-4 py-3">Descrizione</th>
                  <th className="px-4 py-3">Ore</th>
                  <th className="px-4 py-3">Importo</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {data.costi_consulenti.length === 0 && <tr><td colSpan={7} className="px-4 py-6 text-center text-gray-400">Nessun costo imputato</td></tr>}
                {data.costi_consulenti.map(r => (
                  <tr key={r.id} className="border-b last:border-0 hover:bg-gray-50">
                    <td className="px-4 py-3 text-gray-500">{fmtDate(r.data_imputazione)}</td>
                    <td className="px-4 py-3 font-medium">{r.consulente_nome} {r.consulente_cognome}</td>
                    <td className="px-4 py-3 text-gray-500 font-mono">{r.offerta_consulente_numero || '—'}</td>
                    <td className="px-4 py-3">{r.descrizione}</td>
                    <td className="px-4 py-3 text-gray-600">{r.ore}h</td>
                    <td className="px-4 py-3 font-medium text-red-600">{fmt(r.importo)}</td>
                    <td className="px-4 py-3 flex gap-1 justify-end">
                      <button className="btn-secondary btn-sm" onClick={() => openCosto(r)}>Modifica</button>
                      <button className="btn-danger btn-sm" onClick={() => del('costo', r.id)}>Elimina</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal Fattura */}
      {modal?.type === 'fatt' && (
        <Modal title={modal.mode === 'new' ? 'Aggiungi fattura' : 'Modifica fattura'} onClose={() => setModal(null)}>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="label">N. Fattura</label><input className="input" {...f('numero_fattura')} /></div>
            <div><label className="label">Importo (€) *</label><CampoImporto className="input" value={form.importo} onChange={v => setForm(p => ({ ...p, importo: v }))} /></div>
            <div className="col-span-2"><label className="label">Descrizione *</label><input className="input" {...f('descrizione')} /></div>
            {data.ordini?.ha_ordini && (
              <div className="col-span-2">
                <label className="label">Ordine del cliente</label>
                <select className="input" value={form.ordine_id || ''}
                        onChange={e => setForm(p => ({ ...p, ordine_id: e.target.value }))}>
                  <option value="">— Nessuno —</option>
                  {data.ordini.ordini.map(o => (
                    <option key={o.id} value={o.id}>
                      {o.numero}{o.importo ? ` — ${fmt(o.importo)}` : ''}
                      {o.residuo !== null ? ` (residuo ${fmt(o.residuo)})` : ''}
                      {o.stato !== 'attivo' ? ` · ${o.stato}` : ''}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-400 mt-0.5">
                  Il numero va citato in fattura: senza, il cliente può respingerla.
                </p>
              </div>
            )}
            <div>
              <label className="label">Stato</label>
              <select className="input" value={form.stato} onChange={e => setForm(p => ({ ...p, stato: e.target.value }))}>
                {STATI_FATT.map(s => <option key={s} value={s}>{s.replace(/_/g,' ')}</option>)}
              </select>
            </div>
            <div><label className="label">Data emissione</label><input className="input" type="date" {...f('data_emissione')} /></div>
            <div><label className="label">Data scadenza</label><input className="input" type="date" {...f('data_scadenza')} /></div>
            <div><label className="label">Data pagamento</label><input className="input" type="date" {...f('data_pagamento')} /></div>
            <div className="col-span-2"><label className="label">Note</label><textarea className="input" rows={2} {...f('note')} /></div>
          </div>
          <div className="flex justify-end gap-2 mt-5">
            <button className="btn-secondary" onClick={() => setModal(null)}>Annulla</button>
            <button className="btn-primary" onClick={save} disabled={saving}>{saving ? 'Salvo...' : 'Salva'}</button>
          </div>
        </Modal>
      )}

      {/* Modal Offerta Consulente */}
      {modal?.type === 'oc' && (
        <Modal title={modal.mode === 'new' ? 'Aggiungi offerta consulente' : 'Modifica offerta consulente'} onClose={() => setModal(null)}>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Consulente *</label>
              <select className="input" value={form.consulente_id || ''} onChange={e => setForm(p => ({ ...p, consulente_id: e.target.value }))}>
                <option value="">— Seleziona —</option>
                {consulenti.map(c => <option key={c.id} value={c.id}>{c.nome} {c.cognome}</option>)}
              </select>
            </div>
            <div><label className="label">Numero offerta</label><input className="input" {...f('numero')} /></div>
            <div><label className="label">Importo max (€)</label><CampoImporto className="input" value={form.importo_max} onChange={v => setForm(p => ({ ...p, importo_max: v }))} /></div>
            <div>
              <label className="label">Impegno previsto</label>
              <CampoImpegno ore={form.ore_previste} unita={form.unita_previste}
                            onChange={({ ore, unita }) => setForm(p => ({ ...p, ore_previste: ore, unita_previste: unita }))} />
            </div>
            <div><label className="label">Data offerta</label><input className="input" type="date" {...f('data_offerta')} /></div>
            <div>
              <label className="label">Stato</label>
              <select className="input" value={form.stato} onChange={e => setForm(p => ({ ...p, stato: e.target.value }))}>
                {STATI_OC.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase()+s.slice(1)}</option>)}
              </select>
            </div>
            <div className="col-span-2"><label className="label">Descrizione</label><textarea className="input" rows={2} {...f('descrizione')} /></div>
            <div className="col-span-2"><label className="label">Note</label><textarea className="input" rows={2} {...f('note')} /></div>
          </div>
          <div className="flex justify-end gap-2 mt-5">
            <button className="btn-secondary" onClick={() => setModal(null)}>Annulla</button>
            <button className="btn-primary" onClick={save} disabled={saving}>{saving ? 'Salvo...' : 'Salva'}</button>
          </div>
        </Modal>
      )}

      {/* Modal Costo */}
      {modal?.type === 'costo' && (
        <Modal title={modal.mode === 'new' ? 'Imputa costo' : 'Modifica costo'} onClose={() => setModal(null)}>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Consulente *</label>
              <select className="input" value={form.consulente_id || ''} onChange={e => setForm(p => ({ ...p, consulente_id: e.target.value }))}>
                <option value="">— Seleziona —</option>
                {consulenti.map(c => <option key={c.id} value={c.id}>{c.nome} {c.cognome}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Offerta consulente (rif.)</label>
              <select className="input" value={form.offerta_consulente_id || ''} onChange={e => setForm(p => ({ ...p, offerta_consulente_id: e.target.value }))}>
                <option value="">— Nessuna —</option>
                {data.offerte_consulenti.map(oc => <option key={oc.id} value={oc.id}>{oc.consulente_nome} {oc.consulente_cognome} — {oc.numero || oc.id}</option>)}
              </select>
            </div>
            <div className="col-span-2"><label className="label">Descrizione *</label><input className="input" {...f('descrizione')} /></div>
            <div><label className="label">Ore</label><input className="input" type="number" step="0.5" {...f('ore')} /></div>
            <div><label className="label">Importo (€) *</label><CampoImporto className="input" value={form.importo} onChange={v => setForm(p => ({ ...p, importo: v }))} /></div>
            <div><label className="label">Data imputazione *</label><input className="input" type="date" {...f('data_imputazione')} /></div>
            <div className="col-span-2"><label className="label">Nota interna</label><textarea className="input" rows={2} {...f('nota_interna')} /></div>
          </div>
          <div className="flex justify-end gap-2 mt-5">
            <button className="btn-secondary" onClick={() => setModal(null)}>Annulla</button>
            <button className="btn-primary" onClick={save} disabled={saving}>{saving ? 'Salvo...' : 'Salva'}</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
